import { FileText, Plus, X } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ProgressBar } from '@/components/ui/progress-bar';
import { Spinner } from '@/components/ui/spinner';
import type { PendingSupportUpload } from '@/pages/support/use-support-ticket-uploads';
import { cn } from '@/utils/cn';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf';

export const SupportAttachmentPicker = ({
  files,
  uploading,
  progress,
  error,
  disabled,
  onFiles,
  onRemove,
  inlineHint = false,
}: {
  files: PendingSupportUpload[];
  uploading: boolean;
  progress?: number;
  error?: string;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  onRemove: (index: number) => void;
  /** Hint sits beside the add tile instead of on its own line. */
  inlineHint?: boolean;
}) => {
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const canAdd = !disabled;

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-wrap items-center gap-2">
        {files.map((file, index) => (
          <li key={`${file.name}-${index}`} className="relative size-16">
            {file.previewUrl ? (
              <img
                src={file.previewUrl}
                alt=""
                className="size-16 rounded-lg object-cover"
              />
            ) : (
              <span className="flex size-16 flex-col items-center justify-center gap-1 rounded-lg border border-border bg-surface-sunken px-1 text-fg-secondary">
                <FileText className="size-4" aria-hidden />
                <span className="w-full truncate text-center text-caption leading-tight">{file.name}</span>
              </span>
            )}
            <button
              type="button"
              className="absolute -end-1 -top-1 flex size-5 items-center justify-center rounded-full border border-border bg-surface text-fg shadow-e1"
              aria-label={tCommon('action.remove')}
              onClick={() => onRemove(index)}
            >
              <X className="size-3" aria-hidden />
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => inputRef.current?.click()}
            aria-label={t('attachments.add')}
            className={cn(
              'flex size-16 items-center justify-center rounded-lg border border-dashed border-border text-fg-secondary transition-colors',
              'hover:border-border-strong hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              !canAdd && 'cursor-not-allowed opacity-50',
            )}
          >
            {uploading ? <Spinner className="size-4" /> : <Plus className="size-5" aria-hidden />}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            disabled={!canAdd}
            className="sr-only"
            onChange={(event) => {
              const chosen = event.target.files ? Array.from(event.target.files) : [];
              event.target.value = '';
              if (chosen.length > 0) onFiles(chosen);
            }}
          />
        </li>
        {inlineHint ? (
          <li className="text-caption text-fg-tertiary">{t('attachments.tileHint')}</li>
        ) : null}
      </ul>
      {uploading && progress !== undefined ? (
        <ProgressBar value={progress} size="sm" label={t('attachments.uploading')} />
      ) : null}
      {error ? <p className="text-caption text-danger">{error}</p> : null}
      {inlineHint ? null : <p className="text-caption text-fg-tertiary">{t('attachments.tileHint')}</p>}
    </div>
  );
};
